import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Check, Mail, Phone, Send, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { DISCLAIMER, aBtn, aBtnLine, aField, bannedWord } from "@/lib/adopt";

const Bubble = ({ m, mine }) => {
  if (m.from === "sistema") return <p className="text-center text-xs text-[#6B6F80] bg-[#F4EBDD] rounded-full px-4 py-2 mx-auto w-fit">{m.text}</p>;
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#E07A5F] text-white rounded-br-md" : "bg-white text-[#3D405B] rounded-bl-md"}`}>
        <p className="whitespace-pre-line text-[15px]">{m.text}</p>
        <p className={`text-[11px] mt-1 ${mine ? "text-white/75" : "text-[#A09A90]"}`}>{new Date(m.at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p>
      </div>
    </div>
  );
};

export default function AdoptChat() {
  const { tid } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [text, setText] = useState("");
  const end = useRef(null);
  const load = () => api.get(`/adopcion-mensajes/${tid}`).then((r) => setT(r.data)).catch(() => setT(false));
  useEffect(() => { if (user) { load(); const i = setInterval(load, 15000); return () => clearInterval(i); } }, [tid, user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [t?.messages?.length]);
  if (user === false) return <Navigate to={`/login?next=${encodeURIComponent(`/adopcion/mensajes/${tid}`)}`} replace />;
  if (t === false) return <p className="text-center py-24" data-testid="chat-missing">Conversación no encontrada.</p>;
  if (!t) return <div className="bg-[#FFF8F0] min-h-screen" />;
  const send = async (e) => {
    e.preventDefault();
    const w = bannedWord(text);
    if (w) return toast.error(`«${w}»: no se permite hablar de dinero ni de venta.`);
    try { await api.post(`/adopcion-mensajes/${tid}`, { text }); setText(""); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const decide = async (decision) => { try { await api.post(`/adopcion-mensajes/${tid}/decision`, { decision }); load(); } catch (err) { toast.error(errMsg(err)); } };
  const me = t.role;
  return (
    <div className="bg-[#FFF8F0] min-h-screen" data-testid="chat-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/adopcion/panel?tab=mensajes" className="text-sm font-bold text-[#E07A5F]" data-testid="chat-back">← Mensajes</Link>
        <div className="bg-white rounded-3xl p-5 mt-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to={`/adopcion/${t.adoption_id}`} className="font-adopt text-2xl text-[#3D405B] hover:text-[#E07A5F]" data-testid="chat-title">{t.title}</Link>
            <p className="text-sm text-[#6B6F80]">{me === "anunciante" ? `Solicitud de ${t.other_name || "un adoptante"}` : `Con ${t.other_name || "el anunciante"}`} · <b data-testid="chat-status">{{ pendiente: "Pendiente de respuesta", aceptada: "Aceptada", rechazada: "Cerrada" }[t.status]}</b></p>
          </div>
          {me === "anunciante" && t.status === "pendiente" && (
            <div className="flex gap-2">
              <button onClick={() => decide("aceptada")} className={aBtn} data-testid="chat-accept"><Check size={16} /> Aceptar</button>
              <button onClick={() => window.confirm("¿Cerrar esta solicitud?") && decide("rechazada")} className={aBtnLine} data-testid="chat-reject"><X size={16} /> Rechazar</button>
            </div>
          )}
        </div>
        {t.status === "aceptada" && (t.phone || t.email) && (
          <div className="bg-[#81B29A]/15 rounded-3xl p-5 mt-4 text-[#2F6B52]" data-testid="chat-contact">
            <p className="font-bold">{me === "adoptante" ? "Datos de contacto del anunciante" : "Email del adoptante"}</p>
            {t.phone && <a href={`tel:${t.phone}`} className="flex items-center gap-2 mt-2 font-bold" data-testid="chat-phone"><Phone size={16} /> {t.phone}</a>}
            {t.email && <a href={`mailto:${t.email}`} className="flex items-center gap-2 mt-1" data-testid="chat-email"><Mail size={16} /> {t.email}</a>}
          </div>
        )}
        {me === "anunciante" && t.status === "pendiente" && <p className="text-sm text-[#6B6F80] mt-4">Si aceptas, esta persona verá tu teléfono y tu email. Tú verás su email.</p>}
        <div className="space-y-3 mt-6" data-testid="chat-messages">
          {t.messages.map((m, i) => <Bubble key={`${m.at}-${i}`} m={m} mine={m.from === me} />)}
          <div ref={end} />
        </div>
        {t.status !== "rechazada" && (
          <form onSubmit={send} className="flex gap-2 mt-6 sticky bottom-3">
            <input required minLength={2} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe un mensaje…" className={`${aField} rounded-full shadow-sm`} data-testid="chat-input" />
            <button className={`${aBtn} px-4`} aria-label="Enviar" data-testid="chat-send"><Send size={18} /></button>
          </form>
        )}
        <p className="text-xs text-[#6B6F80] mt-6">Nunca envíes dinero por adelantado. {DISCLAIMER}</p>
      </div>
    </div>
  );
}
